import type { SourcingProjectView } from "@shared/labs/sourcing";
import type { SourcingConfig } from "@shared/labs/sourcing-config";
import type { SourcingProjectOverrides } from "@shared/labs/sourcing-project-config";
import { ArrowLeft, Check, Info, Loader2, RotateCcw } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useRoute } from "wouter";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { rpcClient } from "@/lib/rpc";
import { cn } from "@/lib/utils";
import {
  type ConfigField,
  CURVE_FIELD,
  FIELDS,
  readField,
  SECTIONS,
  type SectionId,
} from "../config-fields";

/** A level-shape band: score multiplier + hard ceiling at one level-fit offset. */
type CurveBand = { mult: number; cap: number };
type OverrideVal = number | string | boolean | CurveBand[];

// ─── Sparse-override helpers (immutable) ───────────────────────────────────────

function getOverride(
  draft: SourcingProjectOverrides,
  group: string,
  prop: string,
): OverrideVal | undefined {
  const g = (draft as Record<string, Record<string, OverrideVal>>)[group];
  return g?.[prop];
}

/** Set (or, with `undefined`, clear) one override, pruning an emptied group so the
 *  saved payload stays sparse ("no override" ⇒ inherit). */
function withOverride(
  draft: SourcingProjectOverrides,
  group: string,
  prop: string,
  value: OverrideVal | undefined,
): SourcingProjectOverrides {
  const next = { ...draft } as Record<string, Record<string, OverrideVal>>;
  const g = { ...(next[group] ?? {}) };
  if (value === undefined) delete g[prop];
  else g[prop] = value;
  if (Object.keys(g).length === 0) delete next[group];
  else next[group] = g;
  return next as SourcingProjectOverrides;
}

/**
 * Per-project customization — a settings screen mirroring the product-config
 * mockup, scoped to the fields a project may override (the cost-firewall rails and
 * company blocking live elsewhere). Every field defaults to "Herdar" (inherit the
 * product config); unchecking reveals a control seeded from the product value. Saves
 * only the sparse overrides, so an un-touched field keeps tracking product changes.
 */
export default function SourcingProjectCustomize() {
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const [, params] = useRoute("/labs/sourcing/projects/:projectId/customize");
  const projectId = params?.projectId ?? "";

  const [view, setView] = useState<SourcingProjectView | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [draft, setDraft] = useState<SourcingProjectOverrides>({});
  const [activeSection, setActiveSection] = useState<SectionId>("quality");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [saving, setSaving] = useState(false);
  const savedRef = useRef("{}");

  useEffect(() => {
    if (!projectId) return;
    let alive = true;
    rpcClient.labs
      .sourcingProjectGet({ projectId })
      .then((v) => {
        if (!alive) return;
        setView(v);
        setDraft(v.config.overrides);
        savedRef.current = JSON.stringify(v.config.overrides);
        // Reveal the advanced section up-front if the project already overrides it.
        if (v.config.overrides.explore) setShowAdvanced(true);
      })
      .catch((err) => {
        if (alive) setLoadError(err instanceof Error ? err.message : "Erro inesperado");
      });
    return () => {
      alive = false;
    };
  }, [projectId]);

  const backUrl = `/labs/sourcing/projects/${projectId}`;
  const dirty = JSON.stringify(draft) !== savedRef.current;

  const save = useCallback(async () => {
    setSaving(true);
    try {
      await rpcClient.labs.sourcingProjectUpdate({ projectId, config: { overrides: draft } });
      // biome-ignore lint/security/noSecrets: UI copy, not a credential
      toast({ title: "Configurações salvas" });
      navigate(backUrl);
    } catch (err) {
      toast({
        title: "Falha ao salvar",
        description: err instanceof Error ? err.message : "Erro inesperado",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  }, [projectId, draft, navigate, backUrl, toast]);

  if (loadError) {
    return (
      <section className="flex flex-col items-center gap-3 rounded-xl border border-border bg-card px-6 py-12 text-center">
        <p className="text-sm font-medium">Não foi possível abrir as configurações</p>
        <p className="max-w-md text-sm text-muted-foreground">{loadError}</p>
        <Button variant="outline" onClick={() => navigate(backUrl)}>
          <ArrowLeft /> Voltar ao projeto
        </Button>
      </section>
    );
  }
  if (!view) {
    return (
      <div className="flex items-center gap-2 rounded-xl border border-border bg-card px-4 py-10 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Carregando configurações…
      </div>
    );
  }

  const product = view.productConfig;
  const sections = SECTIONS.filter((s) => !s.advanced || showAdvanced);
  const sectionFields = FIELDS.filter((f) => f.section === activeSection);
  const overriddenCount = (id: SectionId) =>
    FIELDS.filter((f) => f.section === id && getOverride(draft, f.group, f.prop) !== undefined)
      .length + (id === "quality" && draft.judge?.levelShape ? 1 : 0);

  return (
    <>
      {/* Header — back link + title + discard/save. */}
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <button
            type="button"
            onClick={() => navigate(backUrl)}
            className="mb-1 inline-flex items-center gap-1 text-[11px] font-semibold uppercase text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft size={12} /> {view.name}
          </button>
          <h1 className="text-lg font-semibold">Customizar projeto</h1>
          <p className="mt-0.5 max-w-2xl text-[13px] text-muted-foreground">
            Ajustes deste projeto. Cada campo <span className="font-medium">herda</span> a
            configuração do produto até você sobrescrevê-lo — uma mudança no produto continua
            valendo para tudo que você não alterou. Custo, infra e limite diário são geridos no
            produto.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" onClick={() => navigate(backUrl)} disabled={saving}>
            Cancelar
          </Button>
          <Button onClick={() => void save()} disabled={saving || !dirty}>
            {saving ? <Loader2 className="animate-spin" /> : <Check />} Salvar
          </Button>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-[13rem_1fr]">
        {/* Section nav. */}
        <nav className="flex flex-col gap-1 md:sticky md:top-4 md:self-start">
          {sections.map((s) => {
            const on = s.id === activeSection;
            const n = overriddenCount(s.id);
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => setActiveSection(s.id)}
                className={cn(
                  "flex items-center justify-between gap-2 rounded-lg px-3 py-2 text-left text-[13px] transition-colors",
                  on
                    ? "bg-foreground/[0.06] font-medium text-foreground"
                    : "text-muted-foreground hover:bg-foreground/[0.03]",
                )}
              >
                {s.label}
                {n > 0 && (
                  <span className="rounded-md bg-primary/10 px-1.5 font-mono text-[10px] font-semibold text-primary">
                    {n}
                  </span>
                )}
              </button>
            );
          })}
          <label className="mt-2 inline-flex items-center gap-2 px-3 text-[12px] text-muted-foreground">
            <Checkbox
              checked={showAdvanced}
              onCheckedChange={(c) => {
                const next = c === true;
                setShowAdvanced(next);
                if (!next && activeSection === "explore") setActiveSection("quality");
              }}
            />
            Mostrar avançado
          </label>
        </nav>

        {/* Fields. */}
        <div className="min-w-0 space-y-1 rounded-xl border border-border bg-card p-2">
          {activeSection === "quality" && (
            <CurveRow product={product} draft={draft} onChange={setDraft} />
          )}
          {sectionFields.map((f) => (
            <FieldRow
              key={`${f.group}.${f.prop}`}
              f={f}
              product={product}
              draft={draft}
              onChange={setDraft}
            />
          ))}
        </div>
      </div>
    </>
  );
}

// ─── One overridable field ──────────────────────────────────────────────────────

function FieldRow({
  f,
  product,
  draft,
  onChange,
}: {
  f: ConfigField;
  product: SourcingConfig;
  draft: SourcingProjectOverrides;
  onChange: (next: SourcingProjectOverrides) => void;
}) {
  const raw = getOverride(draft, f.group, f.prop);
  const overridden = raw !== undefined;
  const inherited = readField(product, f);
  const current = overridden ? raw : inherited;

  const toDisp = (r: number) => (f.toDisplay ? f.toDisplay(r) : r);
  const fromDisp = (d: number) => (f.fromDisplay ? f.fromDisplay(d) : d);
  const set = (v: OverrideVal) => onChange(withOverride(draft, f.group, f.prop, v));
  const toggleInherit = (inherit: boolean) =>
    onChange(
      withOverride(draft, f.group, f.prop, inherit ? undefined : (inherited as OverrideVal)),
    );

  const fmt = (v: OverrideVal): string => {
    if (f.kind === "toggle") return v ? "Ativado" : "Desativado";
    if (f.options) return f.options.find((o) => o.value === v)?.label ?? String(v);
    return `${f.prefix ?? ""}${toDisp(Number(v))}${f.unit ?? ""}`;
  };

  return (
    <div className="rounded-lg px-3 py-3 hover:bg-foreground/[0.02]">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-[13px] font-medium">{f.label}</span>
            {f.badges?.map((b) => (
              <span
                key={b}
                className="rounded bg-muted px-1 font-mono text-[9px] uppercase tracking-wide text-muted-foreground"
              >
                {b}
              </span>
            ))}
          </div>
          <p className="mt-0.5 text-[12px] text-muted-foreground">{f.desc}</p>
        </div>
        <label className="inline-flex shrink-0 items-center gap-1.5 text-[11px] text-muted-foreground">
          <Checkbox checked={!overridden} onCheckedChange={(c) => toggleInherit(c === true)} />
          herdar
        </label>
      </div>

      <div className="mt-2">
        {overridden ? (
          <FieldControl
            f={f}
            value={current as OverrideVal}
            toDisp={toDisp}
            fromDisp={fromDisp}
            onSet={set}
          />
        ) : (
          <span className="inline-flex items-center gap-1.5 text-[12px] text-muted-foreground">
            <Info size={12} /> Herdado:{" "}
            <span className="font-mono text-foreground/80">{fmt(inherited as OverrideVal)}</span>
          </span>
        )}
      </div>
    </div>
  );
}

/** The editable control for an overridden field, by kind. */
function FieldControl({
  f,
  value,
  toDisp,
  fromDisp,
  onSet,
}: {
  f: ConfigField;
  value: OverrideVal;
  toDisp: (r: number) => number;
  fromDisp: (d: number) => number;
  onSet: (v: OverrideVal) => void;
}) {
  if (f.kind === "toggle") {
    return <Switch checked={Boolean(value)} onCheckedChange={(c) => onSet(c)} />;
  }
  if (f.kind === "select") {
    return (
      <Select value={String(value)} onValueChange={(v) => onSet(v)}>
        <SelectTrigger className="h-8 max-w-xs text-[13px]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {f.options?.map((o) => (
            <SelectItem key={String(o.value)} value={String(o.value)}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }
  if (f.kind === "segmented") {
    return (
      <div className="inline-flex rounded-lg border border-border p-0.5">
        {f.options?.map((o) => (
          <button
            key={String(o.value)}
            type="button"
            onClick={() => onSet(o.value)}
            className={cn(
              "rounded-md px-2.5 py-1 text-[12px] transition-colors",
              value === o.value
                ? "bg-foreground/[0.08] font-medium text-foreground"
                : "text-muted-foreground hover:bg-foreground/[0.04]",
            )}
          >
            {o.label}
          </button>
        ))}
      </div>
    );
  }
  const disp = toDisp(Number(value));
  if (f.kind === "slider") {
    return (
      <div className="flex items-center gap-3">
        <Slider
          className="max-w-xs flex-1"
          value={[disp]}
          min={f.min}
          max={f.max}
          step={f.step}
          onValueChange={([v]) => onSet(fromDisp(v))}
        />
        <span className="w-20 shrink-0 font-mono text-[12px] tabular-nums text-foreground/80">
          {f.prefix ?? ""}
          {disp}
          {f.unit ?? ""}
        </span>
      </div>
    );
  }
  // number
  return (
    <div className="inline-flex items-center gap-1.5">
      {f.prefix && <span className="text-[12px] text-muted-foreground">{f.prefix}</span>}
      <Input
        type="number"
        className="h-8 w-32 text-[13px]"
        value={disp}
        min={f.min}
        max={f.max}
        step={f.step}
        onChange={(e) => onSet(fromDisp(Number(e.target.value)))}
      />
      {f.unit && <span className="text-[12px] text-muted-foreground">{f.unit.trim()}</span>}
    </div>
  );
}

// ─── The level-shape curve (5 bands) ──────────────────────────────────────────────

const BAND_LABELS = ["−2 (muito abaixo)", "−1", "0 (alvo)", "+1", "+2 (muito acima)"];

/** The `judge.levelShape` editor — one inherit toggle + 5 rows of mult/cap sliders.
 *  (The mockup's drag-band editor is deferred; these sliders are functionally equal.) */
function CurveRow({
  product,
  draft,
  onChange,
}: {
  product: SourcingConfig;
  draft: SourcingProjectOverrides;
  onChange: (next: SourcingProjectOverrides) => void;
}) {
  const override = draft.judge?.levelShape as CurveBand[] | undefined;
  const overridden = override !== undefined;
  const inherited = product.judge.levelShape as CurveBand[];
  const bands = overridden ? override : inherited;

  const setBands = (next: CurveBand[]) =>
    onChange(withOverride(draft, "judge", "levelShape", next));
  const toggleInherit = (inherit: boolean) =>
    onChange(
      withOverride(
        draft,
        "judge",
        "levelShape",
        inherit ? undefined : inherited.map((b) => ({ ...b })),
      ),
    );
  const setBand = (i: number, key: "mult" | "cap", v: number) =>
    setBands(bands.map((b, j) => (j === i ? { ...b, [key]: v } : b)));

  return (
    <div className="rounded-lg px-3 py-3 hover:bg-foreground/[0.02]">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <span className="text-[13px] font-medium">{CURVE_FIELD.label}</span>
          <p className="mt-0.5 text-[12px] text-muted-foreground">{CURVE_FIELD.desc}</p>
        </div>
        <label className="inline-flex shrink-0 items-center gap-1.5 text-[11px] text-muted-foreground">
          <Checkbox checked={!overridden} onCheckedChange={(c) => toggleInherit(c === true)} />
          herdar
        </label>
      </div>
      {overridden ? (
        <div className="mt-2 space-y-2">
          {bands.map((b, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: fixed 5-band positional curve
            <div key={i} className="flex flex-wrap items-center gap-3">
              <span className="w-32 shrink-0 font-mono text-[11px] text-muted-foreground">
                {BAND_LABELS[i]}
              </span>
              <span className="flex items-center gap-2">
                <span className="text-[11px] text-muted-foreground">mult</span>
                <Slider
                  className="w-28"
                  value={[b.mult]}
                  min={0}
                  max={1}
                  step={0.05}
                  onValueChange={([v]) => setBand(i, "mult", v)}
                />
                <span className="w-8 font-mono text-[11px] tabular-nums">{b.mult.toFixed(2)}</span>
              </span>
              <span className="flex items-center gap-2">
                <span className="text-[11px] text-muted-foreground">teto</span>
                <Slider
                  className="w-28"
                  value={[b.cap]}
                  min={0}
                  max={1}
                  step={0.05}
                  onValueChange={([v]) => setBand(i, "cap", v)}
                />
                <span className="w-8 font-mono text-[11px] tabular-nums">{b.cap.toFixed(2)}</span>
              </span>
            </div>
          ))}
          <button
            type="button"
            onClick={() => toggleInherit(true)}
            className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
          >
            <RotateCcw size={11} /> Voltar ao herdado
          </button>
        </div>
      ) : (
        <span className="mt-2 inline-flex items-center gap-1.5 text-[12px] text-muted-foreground">
          <Info size={12} /> Herdado:{" "}
          <span className="font-mono text-foreground/80">
            {inherited.map((b) => `${b.mult}/${b.cap}`).join(" · ")}
          </span>
        </span>
      )}
    </div>
  );
}
